import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { AvailablePaymentCurrency } from "@simplyk/common";

import { PrizeCard } from "../../components/PrizeCard/PrizeCard";
import { getPrizeCardElementId } from "../../helpers/constants";
import { getDesktopPrizeLayout } from "../../helpers/getDesktopPrizeLayout";
import { TicketingPrizeOutput } from "../../types";

import { PrizeScrollButton } from "./PrizeScrollButton/PrizeScrollButton";

import { useShapes } from "@/canvas/components/BackgroundShapes/ShapesContext";

const CARD_WIDTH = 343;
const CARD_GAP = 8;
const LEFT_BLEED_SPACING = 7.5;

interface DesktopPrizeListProps {
  prizes: TicketingPrizeOutput[];
  currency: AvailablePaymentCurrency;
  focusedPrizeId?: string | null;
  onOpen: (prize: TicketingPrizeOutput) => void;
}

export const DesktopPrizeList: FC<DesktopPrizeListProps> = ({ prizes, currency, focusedPrizeId = null, onOpen }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [rightBleed, setRightBleed] = useState(0);
  const [columnWidth, setColumnWidth] = useState(CARD_WIDTH);
  const [canScrollPrevious, setCanScrollPrevious] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const { backgroundColor } = useShapes();
  const { largePrizes, compactPrizeColumns } = getDesktopPrizeLayout(prizes);
  const cardCount = largePrizes.length + compactPrizeColumns.length;
  const cardsWidth = cardCount * CARD_WIDTH + (cardCount - 1) * CARD_GAP;
  const isScrollable = cardCount > 1 && cardsWidth > columnWidth + rightBleed;

  const updateScrollButtons = useCallback(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }
    setCanScrollPrevious(list.scrollLeft > 1);
    setCanScrollNext(list.scrollLeft + list.clientWidth < list.scrollWidth - 1);
  }, []);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }
    const measure = () => {
      const pageWidth = anchor.ownerDocument.documentElement.clientWidth;
      const { right, width } = anchor.getBoundingClientRect();
      setRightBleed(Math.max(0, pageWidth - right));
      setColumnWidth(width);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(anchor.ownerDocument.documentElement);
    return () => observer.disconnect();
  }, []);

  useEffect(updateScrollButtons, [updateScrollButtons, rightBleed, columnWidth, prizes.length]);

  // The live editor drives the focused prize, so the list scrolls the matching card into view.
  useEffect(() => {
    const list = listRef.current;
    const card = focusedPrizeId ? list?.ownerDocument.getElementById(getPrizeCardElementId(focusedPrizeId)) : null;
    if (!list || !card) {
      return;
    }
    const listRect = list.getBoundingClientRect();
    const { left: cardLeft, right: cardRight } = card.getBoundingClientRect();
    if (cardLeft < listRect.left || cardRight > listRect.right) {
      const paddingLeft = parseFloat(list.ownerDocument.defaultView?.getComputedStyle(list).paddingLeft ?? "0");
      list.scrollBy({ left: cardLeft - listRect.left - paddingLeft, behavior: "smooth" });
    }
  }, [focusedPrizeId, prizes.length]);

  const scrollBy = (direction: 1 | -1) =>
    listRef.current?.scrollBy({ left: direction * columnWidth, behavior: "smooth" });

  return (
    <Box ref={anchorRef} sx={{ position: "relative" }}>
      <Box
        sx={(theme) => ({
          position: "relative",
          marginLeft: theme.spacing(-LEFT_BLEED_SPACING),
          marginRight: `${-rightBleed}px`,
        })}
      >
        {canScrollPrevious && (
          <Box
            aria-hidden
            data-test="prizes-desktop-list-left-fade"
            sx={(theme) => ({
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              width: theme.spacing(LEFT_BLEED_SPACING),
              zIndex: 1,
              pointerEvents: "none",
              background: `linear-gradient(to right, ${backgroundColor}, transparent)`,
            })}
          />
        )}
        {canScrollPrevious && <PrizeScrollButton direction="previous" onClick={() => scrollBy(-1)} />}
        <Box
          ref={listRef}
          onScroll={updateScrollButtons}
          data-test="prizes-desktop-list"
          sx={(theme) => ({
            display: "flex",
            alignItems: "stretch",
            gap: `${CARD_GAP}px`,
            paddingLeft: theme.spacing(LEFT_BLEED_SPACING),
            paddingRight: isScrollable ? `${rightBleed}px` : 0,
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          })}
        >
          {largePrizes.map((prize) => (
            <Box
              key={prize.id}
              sx={{ flex: `0 0 ${prizes.length === 1 ? columnWidth : CARD_WIDTH}px`, display: "flex" }}
            >
              <PrizeCard prize={prize} currency={currency} variant="large" onClick={() => onOpen(prize)} />
            </Box>
          ))}
          {compactPrizeColumns.map((column) => (
            <Stack key={column[0].id} gap={1} sx={{ flex: `0 0 ${CARD_WIDTH}px` }}>
              {column.map((prize) => (
                <PrizeCard
                  key={prize.id}
                  prize={prize}
                  currency={currency}
                  variant="compact"
                  onClick={() => onOpen(prize)}
                />
              ))}
            </Stack>
          ))}
        </Box>
        {canScrollNext && <PrizeScrollButton direction="next" onClick={() => scrollBy(1)} />}
      </Box>
    </Box>
  );
};
